<template>
    <div class="domain-register">
        <el-row :gutter="20">
            <!-- 左侧域名搜索区域 -->
            <el-col :span="16">
                <el-card class="search-area">
                    <h2 class="search-title">{{ $t('domain.registerTitle') }}</h2>
                    <div class="search-box">
                        <div class="domain-search-container">
                            <el-input v-model="searchDomain" :placeholder="$t('domain.searchPlaceholder')"
                                class="domain-search-input" @keyup.enter.native="searchDomains">
                            </el-input>
                            <div class="domain-suffix-selector" @click="showSuffixSelector">
                                <div class="domain-suffix-divider"></div>
                                <span>{{ domainSuffix }}</span>
                                <i class="el-icon-arrow-down" :class="{'arrow-rotate': suffixDialogVisible}"></i>
                            </div>
                            <el-button type="primary" @click="searchDomains" class="search-btn" :loading="isSearching">
                                {{ $t('domain.search') }}
                            </el-button>
                        </div>
                        
                        <!-- 域名后缀选择区域 -->
                        <div class="suffix-selector-area" v-show="suffixDialogVisible">
                            <div class="suffix-grid">
                                <div 
                                    v-for="option in domainSuffixOptions" 
                                    :key="option.value"
                                    class="suffix-item"
                                    :class="{ 'active': domainSuffix === option.value }"
                                    @click="selectDomainSuffix(option.value)">
                                    {{ option.label }}
                                </div>
                                <!-- 如果API未返回数据，显示默认选项 -->
                                <div 
                                    v-if="domainSuffixOptions.length === 0" 
                                    class="suffix-item"
                                    :class="{ 'active': domainSuffix === '.com' }"
                                    @click="selectDomainSuffix('.com')">
                                    .com
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 历史搜索记录 -->
                    <div class="search-history" v-if="searchHistory.length > 0 && searchResults.length === 0 && !isSearching">
                        <div class="history-header">
                            <span>{{ $t('domain.historySearch') }}:</span>
                            <el-button type="text" @click="clearHistory" class="clear-btn">
                                <i class="el-icon-delete"></i>
                            </el-button>
                        </div>
                        <div class="history-tags">
                            <el-tag v-for="(item, index) in searchHistory" :key="index" size="small"
                                @click="useHistorySearch(item)" class="history-tag">
                                {{ item }}
                            </el-tag>
                        </div>
                    </div>

                    <!-- 搜索引导提示界面 -->
                    <div class="search-prompt" v-if="searchResults.length === 0 && !isSearching">
                        <div class="search-icon">
                            <i class="el-icon-search"></i>
                        </div>
                        <h3 class="prompt-title">{{ $t('domain.startSearch') }}</h3>
                        <p class="prompt-desc">{{ $t('domain.searchDesc') }}</p>
                    </div>

                    <!-- 搜索中loading状态 -->
                    <div class="search-loading" v-if="isSearching && searchResults.length === 0">
                        <div class="loading-content">
                            <i class="el-icon-loading"></i>
                            <p class="loading-text">{{ $t('domain.searching') }}</p>
                        </div>
                    </div>

                    <!-- 搜索结果列表 -->
                    <div class="search-results" v-if="searchResults.length > 0" v-loading="isSearching" element-loading-background="rgba(255, 255, 255, 0.7)">
                        <div class="result-header">
                            <h3>{{ $t('domain.searchResults') }}</h3>
                            <div class="result-count">{{ $t('domain.foundCount', { count: searchResults.length }) }}
                            </div>
                        </div>
                        <el-table :data="searchResults" style="width: 100%" class="domain-table"
                            :row-class-name="tableRowClassName">
                            <el-table-column prop="domain" :label="$t('domain.name')" >
                                <template slot-scope="scope">
                                    <div class="domain-name">
                                        <span class="domain-text">
                                            <span :class="{'highlight-match': searchDomain && scope.row.domain.toLowerCase().startsWith(searchDomain.toLowerCase())}">{{ scope.row.domain.split('.')[0] }}</span><!--
                                            --><span :class="{'highlight-match': domainSuffix && scope.row.domain.toLowerCase().endsWith(domainSuffix.toLowerCase())}">{{ '.' + scope.row.domain.split('.').slice(1).join('.') }}</span>
                                        </span>
                                        <el-tooltip v-if="scope.row.popular" effect="dark"
                                            :content="$t('domain.popular')" placement="top">
                                            <span class="popular-tag">{{ $t('domain.hot') }}</span>
                                        </el-tooltip>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column prop="status" :label="$t('domain.status')" width="120" align="center">
                                <template slot-scope="scope">
                                    <el-tag :type="scope.row.available ? 'success' : 'danger'" class="status-tag">
                                        {{ scope.row.available ? $t('domain.available') : $t('domain.unavailable') }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="price" :label="$t('domain.price')" width="150" align="center">
                                <template slot-scope="scope">
                                    <div class="price-container">
                                        <span class="price-value">${{ scope.row.price }}</span>
                                        <span class="price-unit">/{{ $t('domain.year') }}</span>
                                        <el-tooltip v-if="domainPriceData[scope.row.suffix]" effect="dark" placement="top">
                                            <div slot="content">
                                                <div v-if="domainPriceData[scope.row.suffix].register">
                                                    <div v-for="(price, year) in domainPriceData[scope.row.suffix].register" :key="year">
                                                        {{ year }}{{ $t('domain.year') }}: ${{ price.toFixed(2) }}
                                                    </div>
                                                </div>
                                            </div>
                                            <i class="el-icon-info price-info-icon"></i>
                                        </el-tooltip>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('domain.operation')" width="120" align="center">
                                <template slot-scope="scope">
                                    <el-button v-if="scope.row.available" type="primary" size="small"
                                        class="add-cart-btn" @click="addToCart(scope.row)">
                                        {{ $t('domain.addToCart') }}
                                    </el-button>
                                    <el-button v-else type="info" size="small" class="whois-btn"
                                        @click="checkWhois(scope.row.domain)">
                                        Whois
                                    </el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-card>
            </el-col>

            <!-- 右侧购物车区域 -->
            <el-col :span="8">
                <el-card class="cart-area">
                    <div slot="header" class="cart-header">
                        <span class="cart-title">{{ $t('domain.cart') }}</span>
                        <el-button v-if="cartItems.length > 0" type="text" class="clear-cart-btn" @click="clearCart">
                            <i class="el-icon-delete"></i> {{ $t('domain.clearCart') }}
                        </el-button>
                    </div>
                    <div class="cart-content">
                        <div v-if="cartItems.length === 0" class="cart-empty">
                            <i class="el-icon-shopping-cart-2 empty-cart-icon"></i>
                            <p>{{ $t('domain.emptyCart') }}</p>
                        </div>
                        <div v-else class="cart-items">
                            <div v-for="(item, index) in cartItems" :key="index" class="cart-item">
                                <div class="item-info">
                                    <div class="item-domain">{{ item.domain }}</div>
                                    <div class="item-period">
                                        {{ $t('domain.period') }}: 
                                        <el-select v-model="item.period" size="mini" style="width: 80px" @change="updateCartItemPrice(index)">
                                            <el-option 
                                                v-for="year in getAvailablePeriods(item.suffix)" 
                                                :key="year" 
                                                :label="year + $t('domain.year')" 
                                                :value="year">
                                            </el-option>
                                        </el-select>
                                    </div>
                                </div>
                                <div class="item-price-actions">
                                    <div class="item-price">${{ item.price }}</div>
                                    <el-button type="text" class="delete-btn" @click="removeFromCart(index)">
                                        <i class="el-icon-delete"></i>
                                    </el-button>
                                </div>
                            </div>
                            <div class="cart-summary">
                                <div class="summary-row">
                                    <span>{{ $t('domain.itemCount') }}:</span>
                                    <span>{{ cartItems.length }}</span>
                                </div>
                                <div class="summary-row total-row">
                                    <span>{{ $t('domain.total') }}:</span>
                                    <span class="total-price">${{ totalPrice }}</span>
                                </div>
                            </div>
                            <el-button type="primary" class="checkout-btn" @click="checkout">
                                {{ $t('domain.checkout') }}
                            </el-button>
                        </div>
                    </div>
                </el-card>
            </el-col>
        </el-row>

        <!-- Whois查询对话框 -->
        <el-dialog
            :title="$t('domainList.whoisTitle') + (whoisDialog.domain ? ' - ' + whoisDialog.domain : '')"
            :visible.sync="whoisDialog.visible"
            append-to-body
            :modal-append-to-body="false"
            width="800px">
            <div class="whois-dialog" v-loading="whoisDialog.loading">
                <div v-if="whoisDialog.info && typeof whoisDialog.info === 'object'">
                    <el-tabs v-model="whoisDialog.activeTab" type="border-card">
                        <!-- 基本信息标签页 -->
                        <el-tab-pane label="基本信息" name="basic">
                            <div class="tab-header">
                                <i class="el-icon-document"></i>
                                <span>域名基本信息</span>
                            </div>
                            <el-descriptions :column="2" border size="medium">
                                <el-descriptions-item label="域名">{{ whoisDialog.info.domain }}</el-descriptions-item>
                                <el-descriptions-item label="注册时间">{{ whoisDialog.info.register_time }}</el-descriptions-item>
                                <el-descriptions-item label="到期时间">{{ whoisDialog.info.expiration_time }}</el-descriptions-item>
                                <el-descriptions-item label="注册状态">{{ whoisDialog.info.register_status === 2 ? '已注册' : '未注册' }}</el-descriptions-item>
                                <el-descriptions-item label="服务商">{{ whoisDialog.info.service }}</el-descriptions-item>
                                <el-descriptions-item label="添加时间">{{ whoisDialog.info.add_time }}</el-descriptions-item>
                            </el-descriptions>
                        </el-tab-pane>
                        
                        <!-- 注册人信息标签页 -->
                        <el-tab-pane label="注册人信息" name="registrant">
                            <div class="tab-header">
                                <i class="el-icon-user"></i>
                                <span>注册人详细信息</span>
                            </div>
                            <el-descriptions :column="2" border size="medium">
                                <el-descriptions-item label="注册人">{{ whoisDialog.info.name }}</el-descriptions-item>
                                <el-descriptions-item label="注册人(英文)">{{ whoisDialog.info.name_en }}</el-descriptions-item>
                                <el-descriptions-item label="联系人">{{ whoisDialog.info.contact }}</el-descriptions-item>
                                <el-descriptions-item label="联系人(英文)">{{ whoisDialog.info.contact_en }}</el-descriptions-item>
                                <el-descriptions-item label="电子邮箱">{{ whoisDialog.info.email }}</el-descriptions-item>
                                <el-descriptions-item label="手机号码">{{ whoisDialog.info.mobile }}</el-descriptions-item>
                                <el-descriptions-item label="联系地址">{{ whoisDialog.info.address }}</el-descriptions-item>
                                <el-descriptions-item label="联系地址(英文)">{{ whoisDialog.info.address_en }}</el-descriptions-item>
                                <el-descriptions-item label="所在地区">{{ whoisDialog.info.city }}</el-descriptions-item>
                                <el-descriptions-item label="邮政编码">{{ whoisDialog.info.pc }}</el-descriptions-item>
                                <el-descriptions-item label="注册人ID">{{ whoisDialog.info.registrant_id }}</el-descriptions-item>
                                <el-descriptions-item label="证件号码">
                                    <span class="sensitive-info">{{ maskIdCard(whoisDialog.info.id_code) }}</span>
                                </el-descriptions-item>
                            </el-descriptions>
                        </el-tab-pane>
                        
                        <!-- DNS服务器标签页 -->
                        <el-tab-pane label="DNS服务器" name="dns">
                            <div class="tab-header">
                                <i class="el-icon-connection"></i>
                                <span>DNS服务器配置</span>
                            </div>
                            <el-descriptions :column="2" border size="medium">
                                <el-descriptions-item label="DNS服务器1">{{ whoisDialog.info.dns1 || '-' }}</el-descriptions-item>
                                <el-descriptions-item label="DNS服务器2">{{ whoisDialog.info.dns2 || '-' }}</el-descriptions-item>
                                <el-descriptions-item label="DNS服务器3">{{ whoisDialog.info.dns3 || '-' }}</el-descriptions-item>
                                <el-descriptions-item label="DNS服务器4">{{ whoisDialog.info.dns4 || '-' }}</el-descriptions-item>
                                <el-descriptions-item label="DNS服务器5">{{ whoisDialog.info.dns5 || '-' }}</el-descriptions-item>
                                <el-descriptions-item label="DNS服务器6">{{ whoisDialog.info.dns6 || '-' }}</el-descriptions-item>
                            </el-descriptions>
                        </el-tab-pane>
                        
                        <!-- 状态信息标签页 -->
                        <el-tab-pane label="状态信息" name="status">
                            <div class="tab-header">
                                <i class="el-icon-s-flag"></i>
                                <span>域名状态信息</span>
                            </div>
                            <el-descriptions :column="3" border size="medium">
                                <el-descriptions-item label="禁止转移">
                                    <el-tag :type="whoisDialog.info.forbidden_transfer ? 'danger' : 'success'">
                                        {{ whoisDialog.info.forbidden_transfer ? '是' : '否' }}
                                    </el-tag>
                                </el-descriptions-item>
                                <el-descriptions-item label="禁止更新">
                                    <el-tag :type="whoisDialog.info.forbidden_update ? 'danger' : 'success'">
                                        {{ whoisDialog.info.forbidden_update ? '是' : '否' }}
                                    </el-tag>
                                </el-descriptions-item>
                                <el-descriptions-item label="冻结状态">
                                    <el-tag :type="whoisDialog.info.freeze ? 'danger' : 'success'">
                                        {{ whoisDialog.info.freeze ? '是' : '否' }}
                                    </el-tag>
                                </el-descriptions-item>
                            </el-descriptions>
                        </el-tab-pane>
                    </el-tabs>
                </div>
                
                <!-- 处理非对象情况 -->
                <div class="whois-info" v-else-if="whoisDialog.info">
                    <pre>{{ whoisDialog.info }}</pre>
                </div>
                
                <!-- 无数据情况 -->
                <el-empty v-else description="暂无数据"></el-empty>
            </div>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'DomainRegister',
    data() {
        return {
            searchDomain: '',
            domainSuffix: '.com',
            searchResults: [],
            cartItems: [],
            searchHistory: [],
            domainSuffixOptions: [],
            domainPriceData: {},
            suffixDialogVisible: false,
            isSearching: false,
            whoisDialog: {
                visible: false,
                loading: false,
                info: null,
                activeTab: 'basic',
                domain: null
            }
        }
    },
    mounted() {
        // 添加点击页面其他区域关闭后缀选择器的事件监听
        document.addEventListener('click', this.handleOutsideClick);
        
        // 获取URL参数并自动填写搜索框
        this.parseUrlParams();
    },
    beforeDestroy() {
        // 组件销毁前移除事件监听
        document.removeEventListener('click', this.handleOutsideClick);
    },
    computed: {
        totalPrice() {
            return this.cartItems.reduce((total, item) => total + parseFloat(item.price), 0).toFixed(2)
        }
    },
    watch: {
        // 监听购物车变化，确保价格正确显示并保存到本地存储
        'cartItems': {
            handler(newItems) {
                // 确保每个购物车项目都有正确的价格
                newItems.forEach((item, index) => {
                    if (item.period) {
                        this.updateCartItemPrice(index);
                    }
                });
                
                // 保存到本地存储
                this.saveCartToLocalStorage();
            },
            deep: true
        }
    },
    created() {
        // 从本地存储加载搜索历史
        const savedHistory = localStorage.getItem('domainSearchHistory')
        if (savedHistory) {
            try {
                this.searchHistory = JSON.parse(savedHistory)
            } catch (e) {
                this.searchHistory = []
            }
        }
        
        // 从本地存储加载购物车数据
        this.loadCartFromLocalStorage();

        this.loadDomainSuffixs();
    },
    methods: {
        tableRowClassName({ row, rowIndex }) {
            if (row.popular) {
                return 'popular-domain-row'
            }
            return ''
        },
        loadDomainSuffixs() {
            this.axios.post('/admin/domain/getDomainAllPrice', {}, {
                token: true
            }).then(res => {
                console.log(res);
                const { status, data } = res;
                if (status === 200 && data.code === 1) {
                    const list = data.data.list;
                    
                    // 处理域名后缀数据
                    if (list && list.length > 0) {
                        // 保存完整价格数据
                        this.domainPriceData = {};
                        list.forEach(item => {
                            this.domainPriceData[item.suffix] = item.price;
                        });
                        
                        // 更新域名后缀选择器选项
                        this.domainSuffixOptions = list.map(item => ({
                            label: item.suffix,
                            value: item.suffix
                        }));
                        
                        // 如果有选项但当前选择的后缀不在选项中，则设置为第一个选项
                        if (this.domainSuffixOptions.length > 0 && 
                            !this.domainSuffixOptions.some(option => option.value === this.domainSuffix)) {
                            this.domainSuffix = this.domainSuffixOptions[0].value;
                        }
                        
                        // 重新解析URL参数，确保后缀匹配正确
                        const domainParam = this.$route.query.domain;
                        if (domainParam && domainParam.includes('.')) {
                            const dotIndex = domainParam.indexOf('.');
                            const domainSuffix = '.' + domainParam.substring(dotIndex + 1);
                            const suffixExists = this.domainSuffixOptions.some(option => option.value === domainSuffix);
                            if (suffixExists && this.domainSuffix !== domainSuffix) {
                                this.domainSuffix = domainSuffix;
                            }
                        }
                    }
                }
            }).catch(err => {
                console.error('获取域名后缀数据失败', err);
            })
        },
        searchDomains() {
            if (!this.searchDomain) return

            // 设置加载状态
            this.isSearching = true;

            // 保存到搜索历史
            const searchTerm = this.searchDomain + this.domainSuffix
            if (!this.searchHistory.includes(searchTerm)) {
                this.searchHistory.unshift(searchTerm)
                // 限制历史记录数量
                if (this.searchHistory.length > 10) {
                    this.searchHistory = this.searchHistory.slice(0, 10)
                }
                // 保存到本地存储
                localStorage.setItem('domainSearchHistory', JSON.stringify(this.searchHistory))
            }

            let results = [];
            this.axios.post('/admin/domain/search', {
                domain: this.searchDomain,
                suffix: this.domainSuffix
            }).then(res => {
                console.log(res);
                const { status, data } = res;
                if (status === 200 && data.code === 1) {
                    const list = data.data.list;
                    
                    // 处理域名后缀数据
                    if (list && list.length > 0) {
                        for (let i = 0; i < list.length; i++) {
                            const item = list[i];
                            // 从API返回的{name, avail}格式转换为页面使用的格式
                            const domainName = item.name.split('.')[0]; // 获取域名前缀
                            const suffix = '.' + item.name.split('.').slice(1).join('.'); // 获取域名后缀
                            const available = item.avail; // 域名是否可用
                            const popular = false; // 默认不是热门域名，如果API返回热门标识，可以在这里处理
                            
                            // 使用createDomainResult方法创建域名结果对象
                            const domainResult = this.createDomainResult(domainName, suffix, available, popular);
                            results.push(domainResult);
                        }
                        
                        // 更新搜索结果
                        this.searchResults = results;
                    }
                }
            }).catch(err => {
                console.error('域名查询失败', err);
                this.$message.error(this.$t('domain.searchError'));
            }).finally(() => {
                // 无论成功失败，都取消加载状态
                this.isSearching = false;
            });
        },
        useHistorySearch(domain) {
            // 从历史记录中提取域名和后缀
            const dotIndex = domain.indexOf('.')
            if (dotIndex > 0) {
                this.searchDomain = domain.substring(0, dotIndex)
                this.domainSuffix = domain.substring(dotIndex)
                this.searchDomains()
            }
        },
        clearHistory() {
            this.searchHistory = []
            localStorage.removeItem('domainSearchHistory')
        },
        addToCart(domain) {
            // 检查是否已在购物车中
            const exists = this.cartItems.some(item => item.domain === domain.domain)
            if (!exists) {
                // 添加期限属性
                const cartItem = {
                    ...domain,
                    period: 1 // 默认选择1年
                };
                this.cartItems.push(cartItem);
                this.$message.success(this.$t('domain.addedToCart'));
                // 保存到本地存储
                this.saveCartToLocalStorage();
            } else {
                this.$message.warning(this.$t('domain.alreadyInCart'));
            }
        },
        
        /**
         * 获取域名可用的注册期限
         * @param {String} suffix 域名后缀
         * @returns {Array} 可用年限数组
         */
        getAvailablePeriods(suffix) {
            // 默认提供1-10年的选项
            const defaultPeriods = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
            
            // 如果有价格数据，则根据价格数据提供选项
            if (this.domainPriceData[suffix] && this.domainPriceData[suffix].register) {
                return Object.keys(this.domainPriceData[suffix].register)
                    .map(year => parseInt(year))
                    .sort((a, b) => a - b);
            }
            
            return defaultPeriods;
        },
        
        /**
         * 更新购物车项目的价格
         * @param {Number} index 购物车项目索引
         */
        updateCartItemPrice(index) {
            const item = this.cartItems[index];
            if (!item || !item.suffix || !item.period) return;
            
            // 从价格数据中获取对应期限的价格
            if (this.domainPriceData[item.suffix] && 
                this.domainPriceData[item.suffix].register && 
                this.domainPriceData[item.suffix].register[item.period]) {
                
                const price = this.domainPriceData[item.suffix].register[item.period];
                item.price = price.toFixed(2);
            }
        },
        removeFromCart(index) {
            this.cartItems.splice(index, 1)
            this.saveCartToLocalStorage();
        },
        clearCart() {
            this.cartItems = []
            localStorage.removeItem('domainCartItems');
        },
        checkout() {
            // 如果购物车为空，直接返回
            if (this.cartItems.length === 0) return;
            
            // 检查用户是否已登录
            const token = window.sessionStorage.getItem('token');
            if (!token) {
                // 保存购物车数据和当前状态
                localStorage.setItem('domainCartItems', JSON.stringify(this.cartItems));
                localStorage.setItem('checkoutRedirect', 'true');
                
                // 提示用户需要登录
                this.$message.info(this.$t('message.noLogin'));
                
                // 跳转到登录页面，使用path而不是name
                this.$router.push({
                    path: '/login',
                    query: { redirect: 'domain_checkout' }
                });
                return;
            }
            
            // 已登录，将购物车数据保存到本地存储，以便结算页面使用
            localStorage.setItem('domainCartItems', JSON.stringify(this.cartItems));
            
            // 跳转到结算页面，使用完整路径而不是name
            this.$router.push('/domain/checkout');
        },
        checkWhois(domain) {
            this.whoisDialog.domain = domain;
            this.whoisDialog.visible = true;
            this.loadWhoisInfo(domain);
        },
        
        /**
         * 显示或隐藏域名后缀选择区域
         */
        showSuffixSelector() {
            this.suffixDialogVisible = !this.suffixDialogVisible;
        },
        
        /**
         * 选择域名后缀
         * @param {String} suffix 选择的域名后缀
         */
        selectDomainSuffix(suffix) {
            this.domainSuffix = suffix;
            this.suffixDialogVisible = false;
        },
        
        /**
         * 处理点击页面其他区域时关闭后缀选择器
         * @param {Event} event 点击事件
         */
        handleOutsideClick(event) {
            const selectorArea = document.querySelector('.suffix-selector-area');
            const selectorButton = document.querySelector('.domain-suffix-selector');
            
            // 如果点击的不是选择器区域内的元素，也不是选择器按钮，则关闭选择器
            if (this.suffixDialogVisible && 
                selectorArea && 
                !selectorArea.contains(event.target) && 
                selectorButton && 
                !selectorButton.contains(event.target)) {
                this.suffixDialogVisible = false;
            }
        },
        
        /**
         * 获取国际化文本
         * 确保域名后缀选择器的文本正确显示
         */
        getI18nText() {
            return {
                selectSuffix: this.$t('domain.selectSuffix') || '选择域名后缀'
            };
        },
        
        /**
         * 创建域名搜索结果对象
         * @param {String} domainName 域名名称（不含后缀）
         * @param {String} suffix 域名后缀
         * @param {Boolean} available 是否可用
         * @param {Boolean} popular 是否热门
         * @returns {Object} 域名结果对象
         */
        createDomainResult(domainName, suffix, available, popular) {
            const domain = domainName + suffix;
            let price = '88.00'; // 默认价格
            
            // 从价格数据中获取注册价格
            if (this.domainPriceData[suffix] && this.domainPriceData[suffix].register) {
                // 获取1年期的注册价格
                const registerPrice = this.domainPriceData[suffix].register['1'];
                if (registerPrice) {
                    price = registerPrice.toFixed(2);
                }
            }
            
            return {
                domain,
                available,
                price,
                popular,
                suffix
            };
        },
        loadCartFromLocalStorage() {
            const savedCart = localStorage.getItem('domainCartItems');
            if (savedCart) {
                try {
                    this.cartItems = JSON.parse(savedCart);
                } catch (e) {
                    this.cartItems = [];
                }
            }
        },
        saveCartToLocalStorage() {
            localStorage.setItem('domainCartItems', JSON.stringify(this.cartItems));
        },
        loadWhoisInfo(domain) {
            this.whoisDialog.loading = true;
            this.axios.post('/admin/domain/detail', {
                domain: domain
            }, {
                token: true
            }).then(res => {
                console.log(res);
                const { status, data } = res;
                if (status === 200 && data.code === 1) {
                    this.whoisDialog.info = data.data.info;
                }
            }).catch(err => {
                console.error('获取Whois信息失败', err);
                this.$message.error(this.$t('domain.whoisError'));
            }).finally(() => {
                this.whoisDialog.loading = false;
            });
        },
        maskIdCard(idCard) {
            if (!idCard) return '';
            return idCard.slice(0, 4) + '****' + idCard.slice(-4);
        },
        parseUrlParams() {
            // 获取URL中的domain参数
            const domainParam = this.$route.query.domain;
            
            if (domainParam) {
                // 查找第一个点的位置来分离域名前缀和后缀
                const dotIndex = domainParam.indexOf('.');
                
                if (dotIndex > 0 && dotIndex < domainParam.length - 1) {
                    // 分离域名前缀和后缀
                    const domainPrefix = domainParam.substring(0, dotIndex);
                    const domainSuffix = '.' + domainParam.substring(dotIndex + 1);
                    
                    // 填写到搜索框
                    this.searchDomain = domainPrefix;
                    
                    // 检查后缀是否在可选列表中，如果在则设置，否则使用默认值
                    if (this.domainSuffixOptions.length > 0) {
                        const suffixExists = this.domainSuffixOptions.some(option => option.value === domainSuffix);
                        if (suffixExists) {
                            this.domainSuffix = domainSuffix;
                        }
                    } else {
                        // 如果后缀选项还没加载，先设置后缀，等加载完成后再验证
                        this.domainSuffix = domainSuffix;
                    }
                    
                    // 延迟执行搜索，确保页面完全加载
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.searchDomains();
                        }, 500);
                    });
                } else {
                    // 如果没有找到点，则将整个参数作为域名前缀
                    this.searchDomain = domainParam;
                }
            }
        }
    }
}
</script>

<style scoped>
.domain-register {
    padding: 20px;
    background-color: #f5f7fa;
}

.search-area {
    margin-bottom: 20px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.search-title {
    font-size: 20px;
    color: #333;
    margin-bottom: 20px;
    font-weight: 500;
}

.search-box {
    width: 100%;
    /* display: flex; */
    margin-bottom: 20px;
}

.domain-search-container {
    display: flex;
    align-items: center;
    border-radius: 4px;
    overflow: visible;
    border: 1px solid #dcdfe6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    position: relative;
}

.domain-search-container:focus-within {
    border-color: #0396FF;
    /* box-shadow: 0 0 0 2px #0396FF; */
}

/* 确保科技蓝色调统一 */
:root {
    --tech-blue: #0052d9;
    --tech-blue-hover: #0045b8;
    --tech-blue-light: rgba(0, 82, 217, 0.1);
}

.domain-search-input {
    flex: 1;
}

.domain-search-input>>>.el-input__inner {
    height: 44px;
    line-height: 44px;
    font-size: 16px;
    border: none;
}

.domain-search-input>>>.el-input__inner:focus {
    border-color: transparent;
    box-shadow: none;
}

.domain-search-input>>>.el-input__inner:hover {
    border-color: transparent;
}

/* 确保输入框和按钮无间隙 */
.domain-search-input, .domain-suffix-selector, .search-btn {
    margin: 0;
    padding: 0;
}

.domain-search-input {
    padding-left: 10px;
}

.domain-suffix-selector:hover {
    background-color: #e9ecf2;
}

.domain-suffix-divider {
    width: 1px;
    height: 24px;
    background-color: #dcdfe6;
    margin-right: 10px;
}

.domain-suffix-selector {
    display: flex;
    align-items: center;
    padding: 0 15px;
    height: 44px;
    cursor: pointer;
    font-size: 14px;
    color: #606266;
    /* background-color: #f5f7fa; */
    border-left: none;
    min-width: 80px;
    justify-content: center;
}

.domain-suffix-selector i {
    margin-left: 5px;
}

.search-btn {
    height: 44px;
    padding: 0 30px;
    font-size: 16px;
    border-radius: 0px 2px 2px 0;
    background: linear-gradient(90deg, #0396FF, #0D47A1);
    border: none;
    margin: 0;
    border: none;
}

.search-btn:hover, .search-btn:focus {
    background-color: #0396FF;
    border-color: #0396FF;
}

.suffix-selector-area {
    width: 100%;
    margin-top: 0;
    border: 1px solid #dcdfe6;
    border-top: none;
    border-radius: 0 0 4px 4px;
    padding: 15px;
    background-color: white;
    z-index: 1000;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
}

.suffix-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 15px;
}

.suffix-item {
    padding: 10px;
    text-align: center;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s;
    background-color: white;
}

.suffix-item:hover {
    border-color: #0396FF;
    color: #0396FF;
}

.suffix-item.active {
    background-color: #0396FF;
    color: white;
    border-color: #0396FF;
}

.search-history {
    margin-bottom: 20px;
}

.history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    color: #606266;
}

.history-tags {
    display: flex;
    flex-wrap: wrap;
}

.history-tag {
    margin-right: 10px;
    margin-bottom: 10px;
    cursor: pointer;
    background-color: #f0f7ff;
    color: #0052d9;
    border-color: #d9e8ff;
}

.history-tag:hover {
    background-color: #d9e8ff;
    color: #0045b8;
}

.result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
}

.result-header h3 {
    margin: 0;
    font-size: 18px;
    color: #333;
}

.result-count {
    color: #606266;
    font-size: 14px;
}

.domain-table {
    margin-bottom: 20px;
    border-radius: 4px;
}

.domain-name {
    display: flex;
    align-items: center;
    font-size: 1rem;
}

.domain-text {
    font-weight: 500;
    color: #333;
}

.popular-tag {
    display: inline-block;
    padding: 2px 6px;
    margin-left: 10px;
    background-color: #ff9900;
    color: white;
    border-radius: 2px;
    font-size: 12px;
}

.popular-domain-row {
    background-color: #f0f9ff;
}

.status-tag {
    /* padding: 4px 8px; */
}

.price-container {
    display: flex;
    align-items: center;
    justify-content: center;
}

.price-value {
    font-size: 16px;
    font-weight: 500;
    color: #ff6700;
}

.price-unit {
    font-size: 12px;
    color: #909399;
    margin-left: 4px;
}

.price-info-icon {
    font-size: 14px;
    color: #909399;
    margin-left: 5px;
    cursor: pointer;
}

.add-cart-btn {
    background-color: #0052d9;
    border-color: #0052d9;
}

.whois-btn {
    background-color: #909399;
    border-color: #909399;
}

.cart-area {
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.cart-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
}

.clear-cart-btn {
    color: #909399;
}

.cart-empty {
    text-align: center;
    color: #909399;
    padding: 30px 0;
}

.empty-cart-icon {
    font-size: 48px;
    color: #dcdfe6;
    margin-bottom: 10px;
}

.cart-items {
    padding: 10px 0;
}

.cart-item {
    display: flex;
    justify-content: space-between;
    padding: 15px 0;
    border-bottom: 1px solid #ebeef5;
}

.item-info {
    display: flex;
    flex-direction: column;
}

.item-domain {
    font-weight: 500;
    color: #333;
    margin-bottom: 5px;
}

.item-period {
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;
}

.item-period .el-select {
    margin-left: 5px;
}

.item-price-actions {
    display: flex;
    align-items: center;
}

.item-price {
    font-weight: 500;
    color: #ff6700;
    margin-right: 10px;
}

.cart-summary {
    margin-top: 20px;
    padding: 15px 0;
    border-top: 1px dashed #ebeef5;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    color: #606266;
}

.total-row {
    font-weight: 500;
    color: #333;
}

.total-price {
    color: #ff6700;
    font-size: 18px;
}

.checkout-btn {
    width: 100%;
    margin-top: 20px;
    height: 44px;
    font-size: 16px;
    background-color: #0052d9;
    border-color: #0052d9;
}

/* 域名后缀选择器箭头动画 */
.el-icon-arrow-down {
    transition: transform 0.3s ease;
}

.arrow-rotate {
    transform: rotate(180deg);
}

.search-prompt {
    text-align: center;
    padding: 30px 0;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    margin-bottom: 20px;
}

.search-icon {
    font-size: 48px;
    color: #dcdfe6;
    margin-bottom: 10px;
}

.prompt-title {
    font-size: 18px;
    color: #333;
    margin-bottom: 10px;
}

.prompt-desc {
    color: #909399;
    font-size: 14px;
}

.highlight-match {
    color: #67c23a;
    font-weight: bold;
}

.search-loading {
    text-align: center;
    padding: 30px 0;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    margin-bottom: 20px;
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.loading-text {
    color: #909399;
    font-size: 14px;
    margin-top: 10px;
}

.whois-dialog {
    padding: 20px;
}

.whois-info {
    padding: 20px;
}

.tab-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
}

.tab-header i {
    margin-right: 5px;
}

.sensitive-info {
    color: #ff6700;
}
</style>